import { X, ExternalLink, Zap, CheckCircle, XCircle } from 'lucide-react'
import type { ChargepointSummaryWithDistance, SpeedTier } from '@fuelfinder/shared'
import { operatorLabel } from '../lib/format'

interface ChargepointDetailSheetProps {
  chargepoint: ChargepointSummaryWithDistance
  onClose: () => void
}

/**
 * Get color class based on speed tier
 */
function getTierColorClass(tier: SpeedTier): string {
  if (tier === 'rapid') return 'text-brand-500'
  if (tier === 'fast') return 'text-brand-400'
  return 'text-amber-500'
}

/**
 * Format power in kW
 */
function formatPower(kw: number): string {
  if (kw === 0) return 'N/A'
  return `${Math.round(kw)} kW`
}

/**
 * Format distance in miles
 */
function formatDistance(miles: number): string {
  if (miles < 0.1) return '< 0.1 mi'
  return `${miles.toFixed(1)} mi`
}

/**
 * Get Google Maps directions URL
 */
function getDirectionsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
}

/**
 * Render lightning bolt icons for speed tier
 */
function BoltIcons({ tier, size = 16 }: { tier: SpeedTier; size?: number }) {
  const count = tier === 'rapid' ? 3 : tier === 'fast' ? 2 : 1
  return (
    <span className="inline-flex">
      {Array.from({ length: count }, (_, i) => (
        <Zap key={i} size={size} className="fill-current -ml-0.5 first:ml-0" />
      ))}
    </span>
  )
}

/**
 * Bottom sheet showing chargepoint details when a marker is clicked
 */
export function StationDetailSheet({
  chargepoint,
  onClose,
}: ChargepointDetailSheetProps) {
  const tierColorClass = getTierColorClass(chargepoint.speedTier)

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[1001] bg-white dark:bg-slate-900 rounded-t-2xl shadow-2xl max-h-[70vh] overflow-y-auto animate-slide-up">
      {/* Handle bar */}
      <div className="sticky top-0 bg-white dark:bg-slate-900 pt-3 pb-2 px-4">
        <div className="w-12 h-1 bg-slate-300 dark:bg-slate-600 rounded-full mx-auto mb-3" />

        {/* Header with close button */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white truncate">
              {operatorLabel(chargepoint.operator)}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
              {chargepoint.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 min-h-[44px] rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="px-4 pb-6">
        {/* Power and distance row */}
        <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className={`text-3xl font-bold ${tierColorClass} flex items-center gap-1`}>
              <BoltIcons tier={chargepoint.speedTier} size={20} />
              {formatPower(chargepoint.maxPowerKW)}
            </span>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold text-slate-900 dark:text-white">
              {formatDistance(chargepoint.distanceMiles)}
            </p>
            <div className="flex items-center gap-1 text-sm">
              {chargepoint.isOperational ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle size={14} />
                  Operational
                </span>
              ) : (
                <span className="flex items-center gap-1 text-red-500">
                  <XCircle size={14} />
                  Offline
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Usage info */}
        <div className="py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-4 text-sm">
          <span className="text-slate-600 dark:text-slate-300">
            {chargepoint.usageType}
          </span>
        </div>

        {/* Connectors summary */}
        <div className="py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
            Connectors ({chargepoint.totalConnectors})
          </h3>
          <p className="text-sm text-slate-700 dark:text-slate-200">
            {chargepoint.connectorSummary}
          </p>
        </div>

        {/* Address */}
        <div className="py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
            Location
          </h3>
          <p className="text-base text-slate-700 dark:text-slate-200">
            {chargepoint.town} {chargepoint.postcode}
          </p>
        </div>

        {/* Navigate button */}
        <div className="pt-4">
          <a
            href={getDirectionsUrl(chargepoint.lat, chargepoint.lng)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-4 bg-brand-500 hover:bg-brand-600 text-white text-lg font-semibold rounded-lg transition-colors"
          >
            <ExternalLink size={20} />
            Navigate
          </a>
        </div>
      </div>
    </div>
  )
}
